// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { EventsPerUserResp, FeaturesPerUserResp } from '@evolvemep/api-ts';
import { Card, Flex, Stack } from '@mantine/core';

import { isNil } from 'helpers/isNotNil';
import { SummaryCard } from 'modules/Shop/Insights/RevitUsage/components/SummaryCard';
import type { AnalyticsViewProps } from 'modules/Shop/Insights/RevitUsage/types';
import { EventsPerUserChart } from 'modules/Shop/Insights/RevitUsage/views/charts/EventsPerUserChart';
import { FeaturesPerUserChart } from 'modules/Shop/Insights/RevitUsage/views/charts/FeaturesPerUserChart';

const EventPerUserView = (props: AnalyticsViewProps) => {
  const [data, setData] = useState<EventsPerUserResp>();

  const { avgEventsPerUser, avgEventsPerWeek, totalEvents } = data ?? {};

  return (
    <Flex gap="sm" flex={1}>
      <Card flex={5} withBorder>
        <EventsPerUserChart {...props} onDataRealized={setData} />
      </Card>
      <Stack flex={1} gap="sm">
        <SummaryCard title="Avg. Events Per User" value={avgEventsPerUser} loading={isNil(data)} />
        <SummaryCard title="Total Events" value={totalEvents} loading={isNil(data)} />
        <SummaryCard title="Avg. Events Per Week" value={avgEventsPerWeek} loading={isNil(data)} />
      </Stack>
    </Flex>
  );
};

const FeaturesPerUserView = (props: AnalyticsViewProps) => {
  const [data, setData] = useState<FeaturesPerUserResp>();

  const { avgFeaturesPerUser, avgFeaturesPerWeek, totalFeatures } = data ?? {};

  return (
    <Flex gap="sm" flex={1}>
      <Card flex={5} withBorder>
        <FeaturesPerUserChart {...props} onDataRealized={setData} />
      </Card>
      <Stack flex={1} gap="sm">
        <SummaryCard title="Avg. Features Per User" value={avgFeaturesPerUser} loading={isNil(data)} />
        <SummaryCard title="Total Features" value={totalFeatures} loading={isNil(data)} />
        <SummaryCard title="Avg. Features Per Week" value={avgFeaturesPerWeek} loading={isNil(data)} />
      </Stack>
    </Flex>
  );
};

export const FeatureUsageView = (props: AnalyticsViewProps) => (
  <Stack gap="sm" flex={1} style={{ overflowY: 'auto' }}>
    <EventPerUserView {...props} />
    <FeaturesPerUserView {...props} />
  </Stack>
);
